import React, { Component, Fragment } from 'react';
import * as Queries from '../../graphQL/queries/shipmentQueries';
import {
  triggerBolEmail as emailBOL,
  triggerLabelsEmail as emailLabels
} from '../../graphQL/mutations/shipmentMutations';
import { withNamespaces } from 'react-i18next';
import _last from 'lodash/last';
import _get from 'lodash/get';
import { withApollo } from 'react-apollo';

import combineStyles from '../../utils/combineStyles';
import TypographyStyles from '../../styles/typography';
import LayoutStyles from '../../styles/layout';
import SpacingStyles from '../../styles/helpers/spacing';
import { withStyles } from '@material-ui/core/styles';

import ShipmentCalloutAutoTender from '../../components/shipment/Callout/AutoTender';
import ShipmentCalloutNeedsMoreSteps from '../../components/shipment/Callout/NeedsMoreSteps';
import ErrorNotification from '../../components/common/ErrorNotification';
import LoadingIndicator from '../../components/LoadingIndicator';

import ShipmentSummaryMain from '../../components/shipment/summary/Main';
import ShipmentSummaryCarrierInfo from '../../components/shipment/summary/CarrierInfo';

import MultiEmailModal from '../../components/common/MultiEmailModal';
import NotificationSnackbar from '../../components/common/NotificationSnackbar';

import { AppContext } from '../../AppProvider';

import { SHIPMENT_STATUS } from '../../constants/constants';

const styles = () => ({
  infoSection: {
    width: 'calc(50% - 12px)'
  }
});

export class ShipmentSummary extends Component {
  state = {
    loading: true,
    error: null,
    shipment: {},
    displaySendModal: false,
    snackbarOpen: false,
    snackbarMsg: ''
  };

  async componentDidMount() {
    try {
      const {
        match: {
          params: { id }
        },
        client: ApolloClient
      } = this.props;

      const {
        data: {
          shipment: { byId: shipment }
        }
      } = await ApolloClient.query({
        query: Queries.shipmentById,
        variables: { id }
      });

      shipment.pickup = shipment.stops[0];
      shipment.delivery = _last(shipment.stops);

      this.setState({ shipment });
    } catch (error) {
      this.setState({ error });
    } finally {
      this.setState({ loading: false });
    }
  }

  _printBOL = () => {
    const { shipment } = this.state;
    window.open('/BOL/' + shipment.id);
  };
  _printLabels = () => {
    const { shipment } = this.state;
    window.open('/labels/' + shipment.id);
  };
  _sendDocs = () => {
    this.setState({ displaySendModal: true });
  };
  _toggleDisplaySendModal = display => {
    this.setState({ displaySendModal: display });
  };

  _sendDocLinks = async addresses => {
    const {
      shipment: { id: shipmentId }
    } = this.state;
    const { client, t } = this.props;
    const { currentOrgId } = this.context.globalProps;
    try {
      const response = await client.mutate({
        mutation: emailBOL,
        variables: {
          triggerBolEmails: {
            shipmentId,
            orgId: currentOrgId,
            emailAddresses: addresses
          }
        }
      });
      const {
        data: {
          shipment: { triggerBolEmail }
        }
      } = response;
      if (!triggerBolEmail) {
        const {
          data: {
            errors: { message }
          }
        } = response;
        this.setState({
          displaySendModal: false,
          snackbarOpen: true,
          snackbarMsg: `${t('BOL email failed')} : ${message}`
        });
        // don't want to continue, it'll probaby just fail anyway
        return;
      }
      // labels
      const labelResponse = await client.mutate({
        mutation: emailLabels,
        variables: {
          triggerLabelsEmail: {
            shipmentId,
            orgId: currentOrgId,
            emailAddresses: addresses
          }
        }
      });
      const {
        data: {
          shipment: { triggerLabelsEmail }
        }
      } = labelResponse;
      if (triggerLabelsEmail) {
        this.setState({
          snackbarOpen: true,
          snackbarMsg: t('Emails sent'),
          displaySendModal: false
        });
      } else {
        this.setState({
          snackbarOpen: true,
          snackbarMsg: t('Email failed'),
          displaySendModal: false
        });
      }
    } catch (e) {
      this.setState({
        displaySendModal: false,
        snackbarOpen: true,
        snackbarMsg: `${t('Email send failed')} : ${e.message}`
      });
    }
  };

  _handleSnackbarClose = () => {
    this.setState({ snackbarOpen: false, snackbarMsg: '' });
  };

  render() {
    const { shipment, loading, error, displaySendModal } = this.state;
    const { t } = this.props;

    if (loading) {
      return <LoadingIndicator />;
    }

    if (error) {
      return <ErrorNotification message={t('Error loading page')} />;
    }

    const { classes } = this.props;

    // TODO: Change this to be passed in on the querystring
    const autoTendering =
      shipment.shipmentStatusId === SHIPMENT_STATUS.AUTO_TENDERING;

    return (
      <Fragment>
        {autoTendering ? (
          <ShipmentCalloutAutoTender
            printBOL={this._printBOL}
            printLabels={this._printLabels}
            sendDocs={this._sendDocs}
          />
        ) : (
          <ShipmentCalloutNeedsMoreSteps
            printBOL={this._printBOL}
            printLabels={this._printLabels}
            sendDocs={this._sendDocs}
          />
        )}
        <div className={classes.row}>
          <div className={classes.infoSection}>
            <ShipmentSummaryCarrierInfo
              carrier={_get(shipment, 'selectedShipmentRate.carrier', {}) || {}}
              notes={_get(shipment, 'pickup.pickupNotes')}
              autoTendering={autoTendering}
            />
          </div>
          <div className={classes.infoSection}>
            <ShipmentSummaryMain shipment={shipment} />
          </div>
        </div>
        <MultiEmailModal
          open={displaySendModal}
          onCancel={() => {
            this._toggleDisplaySendModal(false);
          }}
          onSave={this._sendDocLinks}
          title={t('Document Recipient(s)')}
        />
        <NotificationSnackbar
          open={this.state.snackbarOpen}
          handleSnackbarClose={this._handleSnackbarClose}
          message={this.state.snackbarMsg}
        />
      </Fragment>
    );
  }
}

const ShipmentSummaryWithTranslations = withNamespaces()(ShipmentSummary);

export default withStyles(
  combineStyles(TypographyStyles, LayoutStyles, SpacingStyles, styles)
)(withApollo(ShipmentSummaryWithTranslations));
ShipmentSummary.contextType = AppContext;
